import { Injectable } from 'angular-ts-decorators';

import { Domain } from '@shared/app/scripts/services/domain';
import type { IConfig } from '@shared/app/scripts/services/config';
import { isEmpty } from 'lodash';

import { OrderTermModel } from '@core/app/scripts/classes/order-term-model';
export { OrderTermModel };

/**
 * Factory in the core application. To take care of the restful endpoints for the
 * order-terms endpoint.
 */
@Injectable('orderTerms')
export class OrderTerms {
  constructor(
    private $q: ng.IQService,
    private $http: ng.IHttpService,
    private config: IConfig,
    private domain: Domain,
    private uuid,
  ) {
    'ngInject';
  }

  /**
   * Generates the orderTerms schema used on the backend
   * ```
   * {
   *   _id: uuid.v4(),
   *   country: '',
   *   language: '',
   *   orderPreviewText1: '',
   *   orderPreviewText2: '',
   *   orderPreviewText3: '',
   *   orderPreviewText4: ''
   * };
   * ```
   * @param {string} country country for the orderTerm
   * @param {string} language language for the orderTerm
   * @returns {Object} model
   */
  newOrderTerm(country: string = '', language: string = '') {
    return new OrderTermModel(this.uuid, country, language);
  }

  /**
   * Method that returns all existing orderTerms
   * {@link coreModule.OrderTerms#orderTermModel model}
   * @param {string} tenantId
   * Tenant id - optional parameter
   * @returns {Array<orderTermModel>} orderTerms
   */
  getAll(tenantId?: string): ng.IPromise<any> {
    let tenantSection = '';

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    const spynlUrl =
      '//' +
      this.domain.sibling(this.config.spynlSubdomain) +
      tenantSection +
      '/order-terms/get';

    return this.$http
      .get(spynlUrl)
      .then((response: any) => response.data.data)
      .catch((error) => this.$q.reject(error.data.message));
  }

  /**
   * Method that saves the given model to the backend.
   * {@link coreModule.OrderTerms#orderTermModel model}
   * @param {orderTermModel} termsAndConditions model you want to save
   * @param {string} tenantId
   * Tenant id - optional parameter
   * @returns {httpPromise} promise based on the http request
   */
  save(
    termsAndConditions: OrderTermModel,
    tenantId?: string,
  ): ng.IPromise<any> {
    let tenantSection = '';

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    const spynlUrl =
      '//' +
      this.domain.sibling(this.config.spynlSubdomain) +
      tenantSection +
      '/order-terms/save';

    return this.$http
      .post(spynlUrl, {
        data: termsAndConditions,
      })
      .then((response: any) => response.data.data)
      .catch((error: any) => this.$q.reject(error.data.message));
  }

  /**
   * method that removes an existing orderTerm from the server.
   * It plucks out the property _id and sends it to the server
   * @param {orderTermModel} termsAndConditions  model you want to remove
   * @param {string} tenantId
   * Tenant id - optional parameter
   * @returns {httpPromise} promise based on the http request
   */
  remove(
    termsAndConditions: OrderTermModel,
    tenantId?: string,
  ): ng.IPromise<any> {
    let tenantSection = '';

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    const spynlUrl =
      '//' +
      this.domain.sibling(this.config.spynlSubdomain) +
      tenantSection +
      '/order-terms/remove';

    return this.$http
      .post(spynlUrl, {
        filter: { _id: termsAndConditions._id },
      })
      .then((response: any) => response.data.data)
      .catch((error: any) => this.$q.reject(error.data.message));
  }

  /**
   * method that returns localized terms and conditions
   * @param {string} country and country two letter ISO code https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2
   * @param {string} language and language two letter ISO code https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes
   * @returns {orderTerms} localized order terms
   */
  getByLocale(country: string, language: string): ng.IPromise<any> {
    //Not yet implemented in spynl
    const spynlUrl =
      '//' +
      this.domain.sibling(this.config.spynlSubdomain) +
      '/order-terms/get';
    return this.$http
      .post(spynlUrl, {
        filter: { country: country, language: language },
      })
      .then((response: any) => {
        if (isEmpty(response.data.data)) {
          return this.$http
            .post(spynlUrl, {
              filter: { country: 'default', language: 'default' },
            })
            .then((response: any) => response.data.data[0])
            .catch((error: any) => this.$q.reject(error.data.message));
        } else {
          return response.data.data[0];
        }
      });
  }

  /**
   * method that returns terms and conditions depending on the status of the sales order
   * @param {string} order the sales order object
   * @returns {orderTerms} localized order terms
   */
  // TODO it's not orderTerms busines to deal with salesOrder status; move it salesOrder
  getByOrderStatus(order): ng.IPromise<any> {
    if (order && order.status === 'draft') {
      return this.getByLocale(
        order.customer.address.country,
        order.customer.language,
      );
    } else {
      // Use order terms saved on order when in complete status
      if (order.orderTerms) {
        return this.$q.when(order.orderTerms);
      } else {
        return this.$q.when({});
      }
    }
  }
}
